import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Contact2, Building2, Handshake, CheckSquare,
  Receipt, DollarSign, FileText, FileSignature, ScrollText, CreditCard, Landmark, BarChart3, PieChart,
  Settings, User, UsersRound, Workflow, ChevronLeft, ChevronRight, FolderTree,
  ClipboardCheck, X, StickyNote, UserSquare2, Layers, ReceiptText, CalendarClock, Wallet,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useSidebarStore } from '@/stores/sidebarStore';
import { useAuthStore } from '@/stores/authStore';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { NavigationSection } from '@/types/navigation';

const routeAliases: Record<string, string> = {
  '/financial/transactions': '/financial/accounts',
};

const navigation: NavigationSection[] = [
  { title: 'Main', items: [{ title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }] },
  { title: 'Me', items: [
    // No `roles`: any signed-in user can open this, and the API returns only
    // their own payslips.
    { title: 'My Payslips', href: '/my-payslips', icon: Wallet },
  ] },
  { title: 'CRM', items: [
    { title: 'Leads', href: '/leads', icon: Users },
    { title: 'Contacts', href: '/contacts', icon: Contact2 },
    { title: 'Companies', href: '/companies', icon: Building2 },
    { title: 'Deals', href: '/deals/pipeline', icon: Handshake },
    { title: 'Tasks', href: '/tasks', icon: CheckSquare },
    { title: 'Notes', href: '/notes', icon: StickyNote },
  ] },
  { title: 'People', items: [
    { title: 'Employees', href: '/people/employees', icon: UserSquare2, roles: ['MANAGER', 'ADMIN'] },
    { title: 'Payroll Runs', href: '/people/payroll-runs', icon: CalendarClock, roles: ['MANAGER', 'ADMIN'] },
    { title: 'Payslips', href: '/people/payslips', icon: ReceiptText, roles: ['MANAGER', 'ADMIN'] },
    { title: 'Pay Components', href: '/people/pay-components', icon: Layers, roles: ['MANAGER', 'ADMIN'] },
  ] },
  { title: 'Billing', items: [
    { title: 'Invoices', href: '/financial/invoices', icon: FileText },
    { title: 'Quotations', href: '/financial/quotations', icon: FileSignature },
    { title: 'Agreements', href: '/financial/agreements', icon: ScrollText },
    { title: 'Payments', href: '/financial/payments', icon: CreditCard },
  ] },
  { title: 'Accounting', items: [
    { title: 'Overview', href: '/financial', icon: DollarSign },
    { title: 'Accounts', href: '/financial/accounts', icon: Landmark },
    { title: 'Expenses', href: '/expenses', icon: Receipt },
    { title: 'Expense Approvals', href: '/expenses/approvals', icon: ClipboardCheck },
    { title: 'Expense Categories', href: '/expenses/categories', icon: FolderTree },
    { title: 'Budgets', href: '/financial/budgets', icon: PieChart },
    { title: 'Reports', href: '/financial/reports', icon: BarChart3 },
  ] },
  { title: 'Settings', items: [
    { title: 'Profile', href: '/settings/profile', icon: User },
    { title: 'Team', href: '/settings/team', icon: UsersRound, roles: ['ADMIN'] },
    { title: 'Pipelines', href: '/settings/pipelines', icon: Workflow, roles: ['ADMIN'] },
    { title: 'Settings', href: '/settings', icon: Settings },
  ] },
];

function useIsActive(href: string) {
  const location = useLocation();
  const allItems = navigation.flatMap((s) => s.items);
  const effectivePath = Object.entries(routeAliases).find(([route]) => location.pathname.startsWith(route))?.[1] ?? location.pathname;

  return (
    effectivePath === href ||
    location.pathname === href ||
    (href !== '/dashboard' &&
      effectivePath.startsWith(href) &&
      !allItems.some((other) => other.href !== href && other.href.length > href.length && effectivePath.startsWith(other.href)))
  );
}

function NavContent({ collapsed, onNavClick }: { collapsed: boolean; onNavClick?: () => void }) {
  const role = useAuthStore((s) => s.user?.role);

  // Nav items have always carried a `roles` allow-list, but nothing read it —
  // so ADMIN-only links were listed for everyone and only blocked on click.
  // Sections that end up empty are dropped so no bare heading is left behind.
  const visible = navigation
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.roles || (role && item.roles.includes(role))),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <nav className="space-y-1 px-2">
      {visible.map((section) => (
        <div key={section.title} className="py-2">
          {!collapsed && (
            <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {section.title}
            </p>
          )}
          {section.items.map((item) => (
            <NavItem key={item.href} item={item} collapsed={collapsed} onNavClick={onNavClick} />
          ))}
        </div>
      ))}
    </nav>
  );
}

function NavItem({ item, collapsed, onNavClick }: { item: NavigationSection['items'][number]; collapsed: boolean; onNavClick?: () => void }) {
  const isActive = useIsActive(item.href);
  const Icon = item.icon;

  const link = (
    <Link
      to={item.href}
      onClick={onNavClick}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        isActive ? 'bg-sidebar-accent text-foreground' : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-foreground',
        collapsed && 'justify-center px-2',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{item.title}</span>}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">{item.title}</TooltipContent>
      </Tooltip>
    );
  }

  return <div>{link}</div>;
}

export function Sidebar() {
  const { isCollapsed, toggle, isMobileOpen, closeMobile } = useSidebarStore();

  return (
    <TooltipProvider delayDuration={0}>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex-col border-r bg-sidebar transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64',
          'hidden lg:flex',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b px-4">
          {!isCollapsed && (
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">Z</div>
              <span className="text-lg font-semibold">ZyFolks OperationsHub</span>
            </Link>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggle}>
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
        <ScrollArea className="flex-1 py-2">
          <NavContent collapsed={isCollapsed} />
        </ScrollArea>
      </aside>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={closeMobile} />
          <aside className="fixed inset-y-0 left-0 z-50 w-64 flex flex-col border-r bg-sidebar">
            <div className="flex h-16 items-center justify-between border-b px-4">
              <Link to="/dashboard" onClick={closeMobile} className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">Z</div>
                <span className="text-lg font-semibold">ZyFolks OperationsHub</span>
              </Link>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={closeMobile}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1 py-2">
              <NavContent collapsed={false} onNavClick={closeMobile} />
            </ScrollArea>
          </aside>
        </div>
      )}
    </TooltipProvider>
  );
}
